import { useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Button, ButtonGroup, Flex, Text } from '@chakra-ui/react';

import DeductionReconciliationRuns from 'app/components/Reconciliations/DeductionReconciliationRuns';
import SalaryReconciliationRuns from 'app/components/Reconciliations/SalaryReconciliationRuns';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';

import {
  RECONCILIATION_TYPE_PARAM,
  ReconciliationType,
  ReconciliationTypeOptions,
} from 'app/constants/salaryReconciliations';

import { useNavigationStore } from 'app/store';

const DESCRIPTIONS: Record<ReconciliationType, string> = {
  deductions:
    'Each run checks the deduction aggregates against the ledger and lists every problem still open.',
  salary:
    "Each run checks every individual's salary accrual balance against what it should be and lists every problem still open.",
};

/**
 * Lists reconciliation runs of one type, deductions or salary accruals, for every company or for the company being
 * managed. The type in the URL wins; without one, the page shows the type last chosen in the reconciliations pages, so
 * a link back to the list, such as the breadcrumb, keeps it.
 */
function Reconciliations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const savedType = useNavigationStore((state) => state.reconciliationType);
  const setSavedType = useNavigationStore((state) => state.setReconciliationType);
  const typeParam = searchParams.get(RECONCILIATION_TYPE_PARAM);
  const type: ReconciliationType = typeParam === 'salary' ? 'salary' : savedType;

  useEffect(() => {
    if (typeParam === 'salary') {
      setSavedType('salary');
    }
  }, [setSavedType, typeParam]);

  const handleTypeChange = (value: ReconciliationType) => {
    setSavedType(value);
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (value === 'salary') {
        params.set(RECONCILIATION_TYPE_PARAM, value);
      } else {
        params.delete(RECONCILIATION_TYPE_PARAM);
      }
      return params;
    });
  };

  return (
    <AuthenticatedPageWrapper title="Reconciliations">
      <Flex flexDir="column" gap="4px">
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          Reconciliations
        </Text>
        <Text color="brand.darkGray">{DESCRIPTIONS[type]}</Text>
      </Flex>

      <ButtonGroup
        mt="20px"
        size="sm"
        isAttached
        variant="outline"
        alignSelf="flex-start"
        data-testid="reconciliation-type"
      >
        {ReconciliationTypeOptions.map((option) => (
          <Button
            key={option.value}
            variant={type === option.value ? 'solid' : 'outline'}
            isActive={type === option.value}
            onClick={() => handleTypeChange(option.value)}
            data-testid={`reconciliation-type-${option.value}`}
          >
            {option.text}
          </Button>
        ))}
      </ButtonGroup>

      <Flex w="full" mt="20px">
        {type === 'salary' ? <SalaryReconciliationRuns /> : <DeductionReconciliationRuns />}
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default Reconciliations;
